import type { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useDebounceFn, useMount } from 'ahooks'
import { useCallback, useMemo, useRef, useState } from 'react'
import { FileTypeEnum } from 'utils'

import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import MediaPlayer from 'components/MediaPlayer'
import { setElement, setScrollWait } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { useUrlByUsage, useVideoAndAudioUrl } from 'utils/files'
import { prepareUrl } from 'utils/websiteValidation'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import * as s from './Media.module.scss'
import type { MediaElementType } from './mediaTypes'

const Element: MediaElementType = ({
  element: { value, type, id },
  mode,
  styles,
  block,
  font,
  state,
  setState,
  isV2,
}) => {
  const {
    autoPlay,
    controls,
    repeat,
    volume,
    timePoints,
    playbackRate,
    file,
    coverImage,
    percent = 0,
    captions,
    enableCCByDefault,
    overlay,
    disableSeeking,
    displayPlayback,
  } = value
  const dispatch = useProjectDispatch()
  const breakpoints = file?.params?.breakpoints
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: type as typeof CONFIG_ELEMENTS.audio.type | typeof CONFIG_ELEMENTS.video.type,
      elementId: id,
    },
  )

  const setCompleted = useElementCompleted({
    block,
    elementId: id,
    deviceMode: mode.deviceMode,
    completed: false,
  })

  const coverUrl = useUrlByUsage(coverImage, FileUsageTypeEnum.image)
  const fileUrl = useVideoAndAudioUrl(file)

  const { url, cover } = useMemo(() => {
    return {
      url: fileUrl ? prepareUrl(fileUrl) : null, // todo: move prepare to useUrlByUsage
      cover: {
        path: coverUrl,
        overlay: overlay || undefined,
      },
    }
  }, [coverUrl, fileUrl, overlay])
  const [isViewed, setIsViewed] = useState(false)
  const fullyWatched = Boolean(
    state?.maxPlayedSeconds && state?.duration && state.maxPlayedSeconds / state.duration >= 0.98,
  )
  const stateRef = useRef(state)
  stateRef.current = state

  const rootStyles = useElementRootStyles(styles.root)

  const onDuration = useCallback(
    (duration: number, d: number) =>
      dispatch(setElement({ id, value: { ...value, duration, originalDuration: d } })),
    [dispatch, id, value],
  )

  const onPlay = useCallback(
    (position: number, duration: number) => {
      const data = entityAnalyticsConfig.played({
        resultExtensions: {
          startTime: new Date().toISOString(),
          volume,
          playbackRate,
          position,
          captionsEnabled: Boolean(enableCCByDefault),
        },
        objectExtensions: { duration, ...baseObjectExtensions },
        elementId: id,
      })
      if (duration > 0) {
        sendXapiData(data)
      }
      if (percent === 0) {
        setCompleted(true)
      }
    },
    [
      entityAnalyticsConfig,
      enableCCByDefault,
      id,
      percent,
      playbackRate,
      setCompleted,
      volume,
      baseObjectExtensions,
      sendXapiData,
    ],
  )

  const onPauseSetXapi = useCallback(
    (position: number, duration: number) => {
      const data = entityAnalyticsConfig.paused({
        resultExtensions: { position, endTime: new Date().toISOString() },
        objectExtensions: { duration, ...baseObjectExtensions },
        elementId: id,
      })
      if (duration > 0) {
        sendXapiData(data)
      }
    },
    [entityAnalyticsConfig, id, baseObjectExtensions, sendXapiData],
  )

  const { run: onVolume } = useDebounceFn(
    (volume: number, duration: number) => {
      const data = entityAnalyticsConfig.updated({
        resultExtensions: {
          timestamp: new Date().toISOString(),
          volume,
          playbackRate,
          captionsEnabled: Boolean(enableCCByDefault),
        },
        objectExtensions: { ...baseObjectExtensions, duration },
        elementId: id,
      })
      sendXapiData(data)
    },
    { wait: 150 },
  )

  const onPlaybackRate = useCallback(
    (playbackRate: number, duration: number) => {
      const data = entityAnalyticsConfig.updated({
        resultExtensions: {
          timestamp: new Date().toISOString(),
          volume,
          playbackRate,
          captionsEnabled: Boolean(enableCCByDefault),
        },
        objectExtensions: { ...baseObjectExtensions, duration },
        elementId: id,
      })
      sendXapiData(data)
    },
    [entityAnalyticsConfig, enableCCByDefault, id, volume, baseObjectExtensions, sendXapiData],
  )

  const handleOnReady = useCallback(() => {
    dispatch(setScrollWait({ id, ready: true }))
  }, [dispatch, id])

  const onSeekXapi = useCallback(
    ({ seekFrom, seekTo, duration }: { seekFrom: number; seekTo: number; duration: number }) => {
      const data = entityAnalyticsConfig.updated({
        resultExtensions: {
          positionFrom: seekFrom,
          positionTo: seekTo,
          timestamp: new Date().toISOString(),
          volume,
          playbackRate,
          captionsEnabled: Boolean(enableCCByDefault),
        },
        objectExtensions: { ...baseObjectExtensions, duration },
        elementId: id,
      })
      if (duration > 0) {
        sendXapiData(data)
      }
    },
    [
      entityAnalyticsConfig,
      baseObjectExtensions,
      enableCCByDefault,
      id,
      playbackRate,
      volume,
      sendXapiData,
    ],
  )

  const handleProgress = useCallback(
    (progressPercent: number, duration: number) => {
      if (!isViewed && progressPercent >= percent && percent > 0) {
        setIsViewed(true)
        setCompleted(true)

        // Send xAPI event for video/audio completion (with extra completed flag)
        const data = entityAnalyticsConfig.updated({
          resultExtensions: {
            timestamp: new Date().toISOString(),
            volume,
            playbackRate,
            captionsEnabled: Boolean(enableCCByDefault),
          },
          objectExtensions: {
            ...baseObjectExtensions,
            duration,
          },
          elementId: id,
        })
        sendXapiData(data)
      }
    },
    [
      isViewed,
      percent,
      setCompleted,
      entityAnalyticsConfig,
      volume,
      playbackRate,
      enableCCByDefault,
      baseObjectExtensions,
      id,
      sendXapiData,
    ],
  )

  useMount(() => {
    if (url && url !== '//') {
      dispatch(setScrollWait({ id, ready: false }))
    }
  })

  const { run: onSavePlaybackPosition } = useDebounceFn(
    (maxPlayed: number) => {
      if (stateRef.current) {
        setState?.({ ...stateRef.current, maxPlayedSeconds: maxPlayed })
      }
    },
    { wait: 500 },
  )

  const captionsUrl = useUrlByUsage(captions, FileUsageTypeEnum.captions)

  return (
    <div className={s.root} style={rootStyles}>
      <MediaPlayer
        accessibility={type === FileTypeEnum.VIDEO ? value.coverImage?.accessibility : undefined}
        autoPlay={autoPlay && [PreviewMode.preview, PreviewMode.scorm].includes(mode.previewMode)}
        breakpoints={breakpoints}
        captions={captionsUrl}
        controls={controls}
        cover={cover}
        disableSeeking={disableSeeking && !fullyWatched}
        displayPlayback={displayPlayback}
        duration={state?.duration}
        elementId={id}
        enableCCByDefault={enableCCByDefault}
        fonts={font}
        isEditor={mode.previewMode === PreviewMode.editor}
        isV2={isV2}
        loop={repeat}
        onDuration={onDuration}
        onPause={onPauseSetXapi}
        onPlay={onPlay}
        onPlaybackRate={onPlaybackRate}
        onProgress={handleProgress}
        onReady={handleOnReady}
        onSavePlaybackPosition={onSavePlaybackPosition}
        onSeek={onSeekXapi}
        onVolume={onVolume}
        playbackRate={playbackRate}
        savedMaxPlayedSeconds={state?.maxPlayedSeconds}
        sourceType={file?.source}
        start={breakpoints?.[0] || 0}
        styles={styles}
        timePoints={timePoints}
        type={type === FileTypeEnum.AUDIO ? FileTypeEnum.AUDIO : FileTypeEnum.VIDEO}
        url={url}
        volume={volume / 100}
        full
        isCourse
      />
    </div>
  )
}

export default Element
